import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  documentDownloadDtoSchema,
  documentDtoSchema,
  documentListDtoSchema,
  documentUploadTargetDtoSchema,
  type DocumentDownloadDto,
  type DocumentDto,
  type DocumentListDto,
  type DocumentUploadTargetDto,
} from '@lib/dto/document.dto'
import type {
  CreateDocumentInput,
  ListDocumentsQuery,
  UpdateDocumentInput,
} from '@lib/input/document.input'
import {
  MAX_DOCUMENT_BYTES,
  isAllowedDocumentContentType,
} from '@lib/constants/documents'
import type {
  IDocumentService,
  UploadFileMeta,
} from '@/types/DocumentService.types'
import { uploadWithProgress } from '@/helpers/uploadWithProgress'
import { withUploadRetry } from '@/helpers/retryUpload'
import { toQueryString } from '@/helpers/queryString'

/** Generous, but finite — `fetch` has no timeout, which is the problem. */
const FILE_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

/**
 * Service for documents, including the direct-to-storage upload.
 *
 * `uploadFile` runs the three-step handshake and is the only place in the web
 * app that calls `fetch` outside the `api` client. That is deliberate and
 * necessary: the middle step PUTs to Cloudflare R2, and the `api` client
 * attaches our `Authorization: Bearer` header to every request. Sending a
 * session token to a third-party host would leak it, so this one request has to
 * bypass the client. It never reads a JSON body, so nothing is laundered past
 * the schema boundary either.
 */
class DocumentService implements IDocumentService {
  async list(
    query: Partial<ListDocumentsQuery> = {},
  ): Promise<DocumentListDto> {
    try {
      return await api.get(
        `/documents${toQueryString(query)}`,
        documentListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list documents', { error: toErrorMessage(error) })
      throw error
    }
  }

  async createUploadTarget(
    input: CreateDocumentInput,
  ): Promise<DocumentUploadTargetDto> {
    try {
      return await api.post(
        '/documents/upload-url',
        input,
        documentUploadTargetDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to reserve an upload', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async confirm(id: string): Promise<DocumentDto> {
    try {
      return await api.post(
        `/documents/${encodeURIComponent(id)}/confirm`,
        {},
        documentDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to confirm an upload', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Reserve, upload, confirm. Validated locally first so an unusable file is
   * refused before it is sent rather than after — the API enforces the same
   * rules regardless, because a browser check is a courtesy, not a control.
   */
  async uploadFile(file: File, meta: UploadFileMeta): Promise<DocumentDto> {
    if (file.size === 0) {
      throw new Error('That file is empty.')
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      throw new Error(
        `Files must be ${Math.floor(MAX_DOCUMENT_BYTES / (1024 * 1024))} MB or smaller.`,
      )
    }
    if (!isAllowedDocumentContentType(file.type)) {
      throw new Error(
        file.type === ''
          ? 'That file type could not be identified.'
          : `${file.type} files are not supported.`,
      )
    }

    // `contentType` is narrowed to the allowed union by the guard above, so no
    // assertion is needed here. The API re-validates regardless.
    const contentType = file.type
    const target = await this.createUploadTarget({
      kind: meta.kind,
      fileName: file.name,
      contentType,
      byteSize: file.size,
      title: meta.title ?? null,
      clientId: meta.clientId ?? null,
      siteId: meta.siteId ?? null,
      treeId: meta.treeId ?? null,
      treeProblemId: meta.treeProblemId ?? null,
      waterSourceId: meta.waterSourceId ?? null,
      irrigationTimerId: meta.irrigationTimerId ?? null,
      irrigationEquipmentId: meta.irrigationEquipmentId ?? null,
      irrigationProblemId: meta.irrigationProblemId ?? null,
      // Left out rather than null when the caller did not say: the server
      // then uses the site's current visit, as it did before visits.
      ...(meta.visitId === undefined ? {} : { visitId: meta.visitId }),
    })

    /*
     * Retried against the SAME url, unlike a video's.
     *
     * A presigned PUT is idempotent — same key, same bytes, same object — so
     * repeating it is safe and costs nothing but the transfer. A Stream
     * direct-upload URL is one-shot, which is why that path re-reserves
     * instead. The signature outlives a couple of retries comfortably.
     */
    await withUploadRetry(
      async () => {
        // XHR, not fetch: see `uploadWithProgress`. Our bearer token is never
        // attached, so it cannot reach the storage host.
        await uploadWithProgress({
          url: target.uploadUrl,
          method: 'PUT',
          body: file,
          headers: target.uploadHeaders,
          timeoutMs: FILE_UPLOAD_TIMEOUT_MS,
          ...(meta.signal ? { signal: meta.signal } : {}),
          ...(meta.onProgress ? { onProgress: meta.onProgress } : {}),
        })
      },
      { label: `document:${target.document.id}` },
    )

    return this.confirm(target.document.id)
  }

  async getDownloadUrl(id: string): Promise<DocumentDownloadDto> {
    try {
      return await api.get(
        `/documents/${encodeURIComponent(id)}/download-url`,
        documentDownloadDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to get a download link', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(id: string, input: UpdateDocumentInput): Promise<DocumentDto> {
    try {
      return await api.put(
        `/documents/${encodeURIComponent(id)}`,
        input,
        documentDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update a document', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setArchived(id: string, archived: boolean): Promise<DocumentDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/documents/${encodeURIComponent(id)}/${action}`,
        {},
        documentDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} a document`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const documentService = new DocumentService()
